import React, { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { useCurrency } from '../../context/CurrencyContext';
import ApprovalStatusBadge from '../approval/ApprovalStatusBadge';

const getItemImage = (item) => {
  if (item.merchandise?.thumbnail?.linkLocalizations?.[0]?.mediaLink)
    return item.merchandise.thumbnail.linkLocalizations[0].mediaLink;
  if (item.ticket?.thumbnail?.linkLocalizations?.[0]?.mediaLink)
    return item.ticket.thumbnail.linkLocalizations[0].mediaLink;
  if (item.badge?.media?.linkLocalizations?.[0]?.mediaLink)
    return item.badge.media.linkLocalizations[0].mediaLink;
  if (item.avatar?.thumbnail?.linkLocalizations?.[0]?.mediaLink)
    return item.avatar.thumbnail.linkLocalizations[0].mediaLink;
  return null;
};

const getItemName = (item) =>
  item.nameLocalizations?.values?.[0]?.value ?? item.name ?? '—';

/**
 * Same visual language as ItemCard (image / type badge / name / price) but with a footer
 * built for the Free Trial flow instead of Edit/Delete: either "+ Add to Free Trial" for an
 * unlisted item, or an approval-status badge + Remove for one already on the list.
 */
const FreeTrialItemCard = ({ item, itemTypes = [], freeTrialEntry, onAdd, onRemove, canEdit = true, busy = false }) => {
  const { activeCurrency, currencies } = useCurrency();
  const itemCurrencyId   = item.prices?.[0]?.currencyId ?? null;
  const resolvedCurrency = (itemCurrencyId ? currencies.find(c => c.id === itemCurrencyId) : null) ?? activeCurrency;
  const decimalPlaces    = resolvedCurrency?.decimalPlaces ?? 2;
  const factor           = Math.pow(10, decimalPlaces);
  const symbol           = resolvedCurrency?.symbol ?? '';
  const baseCostMinor    = item.prices?.[0]?.baseCost ?? item.baseCost ?? 0;
  const baseCost         = baseCostMinor / factor;

  const imageUrl = getItemImage(item);
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [imageUrl]);

  const typeLabel = itemTypes.find(t => t.id === item.itemTypeId)?.Type ?? '—';
  const itemName  = getItemName(item);
  const isAdded   = !!freeTrialEntry;

  return (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: '12px',
        border: '1px solid rgba(0,0,0,0.07)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 16px 28px -8px rgba(13,35,83,0.2)' },
      }}
    >
      <Box sx={{ position: 'relative', height: 140, bgcolor: '#e2e8f0', overflow: 'hidden', flexShrink: 0 }}>
        {imageUrl && !imgError ? (
          <Box
            component="img"
            src={imageUrl}
            alt={itemName}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <ShoppingBagIcon sx={{ fontSize: 40, color: '#94a3b8' }} />
          </Box>
        )}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.45)', p: 1 }}>
          <Box sx={{ display: 'inline-block', px: 1, py: 0.3, borderRadius: '6px', bgcolor: 'rgba(59,130,246,0.8)', fontSize: 9, fontWeight: 700, color: 'white', lineHeight: 1.4 }}>
            {typeLabel}
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 1.75, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography fontWeight={700} fontSize={13} sx={{ color: '#1e293b', lineHeight: 1.35 }}>
          {itemName}
        </Typography>
        <Typography fontWeight={800} fontSize={16} sx={{ color: '#1e3a5f' }}>
          {symbol}{baseCost.toFixed(decimalPlaces)}
        </Typography>
      </Box>

      <Box sx={{ px: 1.75, py: 1.25, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        {isAdded ? (
          <>
            <ApprovalStatusBadge
              status={freeTrialEntry.contentStatus}
              actionType={freeTrialEntry.approvalActionType}
            />
            {canEdit && (
              <Button
                size="small"
                startIcon={<RemoveIcon sx={{ fontSize: 13 }} />}
                onClick={() => onRemove(item.itemId)}
                disabled={busy}
                sx={{
                  color: '#ef4444', bgcolor: '#fff1f2', borderRadius: '8px', px: 1.25, py: 0.4,
                  fontSize: 11, fontWeight: 700, textTransform: 'none',
                  '&:hover': { bgcolor: '#fee2e2' },
                }}
              >
                Remove
              </Button>
            )}
          </>
        ) : (
          canEdit && (
            <Button
              fullWidth
              size="small"
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={() => onAdd(item.itemId)}
              disabled={busy}
              sx={{
                background: '#059669', color: 'white', fontWeight: 700, fontSize: 11.5,
                textTransform: 'none', borderRadius: '8px', py: 0.6, boxShadow: 'none',
                '&:hover': { background: '#047857', boxShadow: 'none' },
              }}
            >
              Add to Free Trial
            </Button>
          )
        )}
      </Box>
    </Box>
  );
};

export default FreeTrialItemCard;
